<script>
import CodeEditor from '@/components/form-controls/CodeEditor';
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import SidebarButtonGroup from '@/components/layouts/dashboard/SidebarButtonGroup';
import SidebarLink from '@/components/layouts/dashboard/SidebarLink';
import SidebarLinkGroup from '@/components/layouts/dashboard/SidebarLinkGroup';
import getErrorMessage from '@/modules/server-errors';

export default {
  name: 'EditSource',
  components: {
    CodeEditor,
    DashboardLayout,
    SidebarButtonGroup,
    SidebarLink,
    SidebarLinkGroup
  },
  metaInfo() {
    return { title: this.title };
  },
  data() {
    return {
      title: '',
      breadcrumbs: [],
      errorMessage: '',
      isBeautifying: false,
      isLoadingSource: true,
      isSaving: false,
      lineWrapping: false,
      link: {
        href: '',
        target: '',
        title: ''
      },
      page: {},
      savedSource: '',
      site: {},
      source: '',
      statusCode: null
    };
  },
  computed: {
    pageUrl() {
      const url = (this.site.url || '').replace(/\/+$/, '');
      const path = (this.page.path || '').replace(/^\/+/, '');
      return url + '/' + path;
    }
  },
  created() {
    // Fetch prefs
    this.lineWrapping = sessionStorage.getItem('codeEditorLineWrapping') === 'true';

    // Fetch initial data
    Promise.all([
      this.$http.get(`/sites/${this.$route.params.siteId}`),
      this.$http.get(`/sites/${this.$route.params.siteId}/pages/${this.$route.params.pageId}`)
    ])
      .then(res => {
        this.site = res[0].data.site;
        this.page = res[1].data.page;
        this.title = this.$t('Edit Source');
        this.breadcrumbs = [
          { text: this.$t('Sites'), to: { name: 'Sites' }},
          { text: this.site.name, to: { name: 'Site', params: { siteId: this.site.id }}},
          { text: this.page.name, to: { name: 'EditPage', params: { siteId: this.site.id, pageId: this.page.id }}},
          { text: this.$t('Edit Source') }
        ];
        this.statusCode = 200;

        // Bump to the site view if the site is on lockdown
        if (this.site.isLocked) {
          this.$router.push({ name: 'Site', params: { siteId: this.site.id }});
          return;
        }
      })
      .catch(err => this.statusCode = err.response.status);

    // Fetch the source independently since the user's server will take more time to respond
    this.$http.get(`/sites/${this.$route.params.siteId}/pages/${this.$route.params.pageId}/source`)
      .then(res => {
        this.savedSource = res.data.source;
        this.source = res.data.source;

        if (this.$refs.codeEditor) {
          this.$nextTick(() => this.$refs.codeEditor.clearHistory());
        }
      })
      .catch(err => {
        this.errorMessage = getErrorMessage(err.response.data.code);

        // Make the editor readonly when we can't load the source
        if (this.$refs.codeEditor) {
          this.$refs.codeEditor.setOption('readOnly', true);
        }
      })
      .finally(() => this.isLoadingSource = false);

  },
  beforeRouteLeave(to, from, next) {
    // Check for unsaved changes
    if (this.source !== this.savedSource) {
      this.$confirm(this.$t('Your changes haven\'t been saved. Are you sure you want to continue?'), {
        title: this.$t('Unsaved Changes'),
        confirmButtonText: this.$t('Continue'),
        cancelButtonText: this.$t('Cancel')
      }).then(() => next());
    } else {
      next();
    }
  },
  methods: {
    cleanupCode() {
      this.isBeautifying = true;
      this.$http.post('/utilities/beautify', {
        code: this.source,
        type: 'html'
      })
        .then(res => this.source = res.data.code)
        .catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });        })
        .finally(() => this.isBeautifying = false);
    },

    handleDone() {
      this.$router.push({ name: 'EditPage', params: { siteId: this.site.id, pageId: this.page.id }});
    },

    handleSubmit() {
      const source = this.source;
      this.isLoadingSource = true;
      this.$http.put(`/sites/${this.$route.params.siteId}/pages/${this.$route.params.pageId}/source`, {
        source
      })
        .then(() => {
          this.savedSource = source;
          this.$router.push({ name: 'EditPage', params: { siteId: this.site.id, pageId: this.page.id }});
        })
        .catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => this.isLoadingSource = false);
    },

    toggleLineWrapping() {
      this.lineWrapping = !this.lineWrapping;
      sessionStorage.setItem('codeEditorLineWrapping', this.lineWrapping.toString());
    }
  }
};
</script>

<template>
  <dashboard-layout :disable-scrolling="true" :status-code="statusCode" size="full">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <!-- Sidebar -->
    <template slot="sidebar">
      <!-- Actions -->
      <sidebar-link-group :title="$t('Actions')">
        <sidebar-link
          :text="$t('Cleanup Code')"
          :loading="isBeautifying"
          icon="el-icon-fa-broom"
          @click="cleanupCode"
        />
        <sidebar-link
          :text="lineWrapping ? $t('Disable Line Wrapping') : $t('Enable Line Wrapping')"
          icon="el-icon-fa-level-down el-icon-fa-rotate-90"
          @click="toggleLineWrapping"
        />
        <sidebar-link
          :text="$t('View Webpage')"
          :href="pageUrl"
          icon="el-icon-fa-external-link"
        />
      </sidebar-link-group>

      <sidebar-button-group>
        <!-- Save -->
        <el-button :loading="isSaving" type="primary" @click="handleSubmit">
          {{ $t('Save Changes') }}
        </el-button>

        <!-- Done -->
        <el-button type="info" @click="handleDone">
          {{ $t('Done') }}
        </el-button>
      </sidebar-button-group>
    </template>

    <!-- Source editor -->
    <div
      v-loading="isLoadingSource"
      v-if="!errorMessage"
      class="source-editor"
    >
      <div class="code-editor">
        <code-editor
          ref="codeEditor"
          v-model="source"
          :allow-full-screen="false"
          :loading="isLoadingSource"
          :line-wrapping="lineWrapping"
          mode="html"
          height="100%"
        />
      </div>
    </div>

    <!-- Error message -->
    <div v-if="errorMessage" class="error-message">
      <div class="error-message-body">
        {{ errorMessage }}
      </div>
    </div>
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.source-editor {
  display: flex;
  position: relative;
  flex: 1 1 auto;
  background: $--color-white;
  overflow: auto;

  .code-editor {
    flex: 1 1 auto;
    border: none;
    border-radius: 0;
    padding: 0;
    margin-bottom: 0;
  }
}

.error-message {
  display: flex;
  flex: 1 1 auto;
  color: $--color-gray-40;
  align-items: center;
  justify-content: center;

  .error-message-body {
    font-size: 1.2rem;
    padding: 1rem;
  }
}
</style>
